"use client";

import { useCallback, useRef, useState, useEffect } from "react";

const LONG_PRESS_DURATION_MS_NORMAL = 600; // for common actions
const LONG_PRESS_DURATION_MS_CRITICAL = 1800; // for critical actions like delete..

export type ThresholdDuration = "normal" | "critical";
export type TresholdDuration = ThresholdDuration;

export type LongPressHandlers = {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
};

export type UseLongPressOptions = {
  onLongPress: () => void;
  threshold?: ThresholdDuration;
  enabled?: boolean;
  preventClickAfterLongPress?: boolean;
  longPressHint?: string; // Text to show in title attribute
};

export type UseLongPressReturn = {
  handlers: LongPressHandlers;
  isLongPressing: boolean;
};

function getLongPressDuration(threshold: ThresholdDuration) {
  return threshold === "normal"
    ? LONG_PRESS_DURATION_MS_NORMAL
    : LONG_PRESS_DURATION_MS_CRITICAL;
}

/**
 * Custom hook for detecting long press gestures
 *
 * @param onLongPress - Callback to execute after long press threshold
 * @param threshold - Duration in ms to trigger long press ("normal"=600ms, "critical"=1800ms)
 * @param enabled - Whether long press detection is enabled (default: true)
 * @param preventClickAfterLongPress - If true, blocks onClick after long press completes (default: false)
 * @param longPressHint - Text to show in title attribute (e.g., "Press long to reset")
 * @returns Event handlers and isLongPressing state for visual feedback
 *
 * @example
 * const longPressHandlers = useLongPress({
 *   onLongPress: handleReset,
 *   threshold: "normal",
 *   enabled: isActive,
 *   preventClickAfterLongPress: true,
 *   longPressHint: "Press long to reset search"
 * });
 *
 * return <div {...longPressHandlers.handlers}>Long press me</div>
 */
export function useLongPress({
  onLongPress,
  threshold = "normal",
  enabled = true,
  preventClickAfterLongPress = false,
  longPressHint,
}: UseLongPressOptions): UseLongPressReturn {
  const [isLongPressing, setIsLongPressing] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsLongPressing(false);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;

      // Prevent default to avoid text selection during long press
      e.preventDefault();

      longPressTriggeredRef.current = false;
      setIsLongPressing(true);
      timerRef.current = setTimeout(
        () => {
          longPressTriggeredRef.current = true;
          onLongPress();
          setIsLongPressing(false);
        },
        getLongPressDuration(threshold),
      );
    },
    [enabled, onLongPress, threshold],
  );

  const onPointerUp = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (preventClickAfterLongPress && longPressTriggeredRef.current) {
        e.preventDefault();
        e.stopPropagation();
        longPressTriggeredRef.current = false;
      }
    },
    [preventClickAfterLongPress],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    handlers: {
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      ...(preventClickAfterLongPress && { onClick }),
      ...(enabled && longPressHint && { title: longPressHint }),
    },
    isLongPressing,
  };
}
